import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/admin/")({
  component: AdminIndex,
});

interface Counts {
  releases: number;
  activations: number;
  wisdom: number;
  wisdom_published: number;
  books: number;
  shop: number;
  orders: number;
  subscriptions_active: number;
  customers: number;
}

const CARDS = [
  { key: "releases", label: "Releases", to: "/admin/releases" },
  { key: "shop", label: "Shop", to: "/admin/shop" },
  { key: "orders", label: "Orders", to: "/admin/shop" },
  { key: "subscriptions_active", label: "Active members", to: "/admin/subscriptions" },
  { key: "customers", label: "Customers", to: "/admin/customers" },
  { key: "wisdom", label: "Wisdom", to: "/admin/wisdom" },
  { key: "books", label: "Books", to: "/admin/books" },
  { key: "activations", label: "Activations", to: "/admin/activations" },
] as const;

function AdminIndex() {
  const [counts, setCounts] = useState<Counts | null>(null);

  useEffect(() => {
    (async () => {
      const [r, a, w, wp, b, s, o, subs, c] = await Promise.all([
        supabase.from("releases").select("*", { count: "exact", head: true }),
        supabase.from("activations").select("*", { count: "exact", head: true }),
        supabase.from("wisdom_posts").select("*", { count: "exact", head: true }),
        supabase.from("wisdom_posts").select("*", { count: "exact", head: true }).eq("published", true),
        supabase.from("books").select("*", { count: "exact", head: true }),
        supabase.from("shop_products").select("*", { count: "exact", head: true }),
        supabase.from("orders").select("*", { count: "exact", head: true }),
        supabase.from("subscriptions").select("*", { count: "exact", head: true }).in("status", ["active", "trialing", "past_due"]),
        supabase.from("profiles").select("*", { count: "exact", head: true }),
      ]);
      setCounts({
        releases: r.count ?? 0,
        activations: a.count ?? 0,
        wisdom: w.count ?? 0,
        wisdom_published: wp.count ?? 0,
        books: b.count ?? 0,
        shop: s.count ?? 0,
        orders: o.count ?? 0,
        subscriptions_active: subs.count ?? 0,
        customers: c.count ?? 0,
      });
    })();
  }, []);

  return (
    <div className="space-y-10">
      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {CARDS.map((c) => (
          <Link
            key={c.key + c.label}
            to={c.to}
            className="group rounded-lg border hairline p-6 transition hover:border-gold"
          >
            <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground">{c.label}</p>
            <p className="mt-3 font-serif text-4xl text-gold-luminous">
              {counts ? counts[c.key as keyof Counts] : "—"}
            </p>
            {c.key === "wisdom" && counts && (
              <p className="mt-2 text-xs text-muted-foreground">
                {counts.wisdom_published} published
              </p>
            )}
            <p className="mt-4 text-xs uppercase tracking-[0.2em] text-gold opacity-0 transition group-hover:opacity-100">
              Manage →
            </p>
          </Link>
        ))}
      </section>

      <section className="rounded-lg border hairline p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="font-serif text-2xl">Content operations</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              All content editing is scoped to the admin role and enforced by row-level security. Publishing a wisdom post makes it visible to the public archive; drafts remain private until then. Subscription cancellations revoke access immediately.
            </p>
          </div>
          <Link
            to="/admin/launch"
            className="shrink-0 rounded-full bg-gold-gradient px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-obsidian"
          >
            Launch readiness →
          </Link>
        </div>
      </section>
    </div>
  );
}
